import React, {
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';

import { useNavigate } from 'react-router-dom';

import {
  LogOut,
  Package,
  ShoppingBag,
  Plus,
  Pencil,
  Trash2,
  Phone,
  MessageCircle,
  RefreshCw,
  Upload,
  KeyRound,
  Search,
  X
,
  Printer
,
  TrendingUp
,
  BarChart3,
  FileSpreadsheet,
  Download,
  Images,
  Store,
  ReceiptText,
  WalletCards
} from 'lucide-react';

import axios from 'axios';

import { imgUrl } from '../data/mockData';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '../components/ui/tabs';

import { Badge } from '../components/ui/badge';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';

import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';

import { toast } from 'sonner';


const API = process.env.REACT_APP_BACKEND_URL;


// ============================================================
// CONSTANTS
// ============================================================

const KNOWN_CATEGORIES = [
  'Fustane',
  'Bluza',
  'Këmisha',
  'Pantallona të Shkurtra',
  'Pantallona të Gjata',
  'Xhaketa',
  'Xhupa',
  'Këpucë',
  'Kapele',
  'Çorape',
  'Të Brendshme',
  'Veshje e Përditshme',
  'Sportive',
  'Aksesorë',
  'Parfume'
];


const SIZE_PRESETS = [
  {
    label: 'Rroba (S-XXL)',
    sizes: ['S', 'M', 'L', 'XL', 'XXL']
  },
  {
    label: 'Këpucë (36-44)',
    sizes: [
      '36',
      '37',
      '38',
      '39',
      '40',
      '41',
      '42',
      '43',
      '44'
    ]
  },
  {
    label: 'Pa masë',
    sizes: ['']
  }
];


const STATUS_LABELS = {
  pending: 'Në Pritje',
  posted: 'Postuar',
  delivered: 'Dorëzuar',
  cancelled: 'Anuluar'
};


const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  posted: 'bg-blue-100 text-blue-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};


const authHeaders = () => ({
  Authorization:
    `Bearer ${localStorage.getItem('lor95_admin_token')}`
});


// ============================================================
// HELPERS
// ============================================================

const createId = () => {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};


const defaultSizeRows = () => (
  ['S', 'M', 'L', 'XL', 'XXL'].map(size => ({
    id: createId(),
    size,
    stock: '0'
  }))
);


const createEmptyColorGroup = () => ({
  id: createId(),
  color: '',
  images: [],
  sizes: defaultSizeRows()
});


const createEmptyProduct = () => ({
  name: '',
  sku: '',
  category: '',
  categories: [],
  gender: 'Femra',
  season: 'Verë',
  priceLek: '',
  salePriceLek: '',

  description: '',

  featured: false,
  newArrival: false,
  bestSeller: false,

  // Sistem i ri
  useVariants: true,
  colorGroups: [
    createEmptyColorGroup()
  ],

  // Sistem i vjetër
  image: '',
  sizesText: 'S, M, L, XL, XXL',
  stock: '10'
});


const productToForm = (product) => {

  const variants = product.variants || [];
  const images = product.images || [];

  // ==========================================================
  // PRODUCT ME VARIANTE
  // ==========================================================

  if (variants.length > 0) {

    const colors = [];

    variants.forEach(variant => {
      const color = variant.color || '';

      if (
        !colors.some(
          c => c.toLowerCase() === color.toLowerCase()
        )
      ) {
        colors.push(color);
      }
    });

    const groups = colors.map(color => {

      const groupVariants = variants.filter(
        variant =>
          (variant.color || '').toLowerCase()
          === color.toLowerCase()
      );

      const groupImages = images
        .filter(
          image =>
            (image.color || '').toLowerCase()
            === color.toLowerCase()
        )
        .map(image => image.url);

      return {
        id: createId(),

        color,

        images: groupImages,

        sizes: groupVariants.map(variant => ({
          id: createId(),
          size: variant.size || '',
          stock: String(
            variant.stock ?? 0
          )
        }))
      };
    });

    return {
      name: product.name || '',
      sku: product.sku || '',
      category: product.category || '',
      categories:
        Array.isArray(product.categories)
        && product.categories.length
          ? product.categories
          : (
              product.category
                ? [product.category]
                : []
            ),
      gender: product.gender || 'Femra',
      season: product.season || 'Verë',

      priceLek: String(
        Math.round(
          (product.price || 0) / 100
        )
      ),

      salePriceLek:
        product.salePrice != null
          ? String(
              Math.round(
                product.salePrice / 100
              )
            )
          : '',

      description:
        product.description || '',

      featured:
        !!product.featured,

      newArrival:
        !!product.newArrival,

      bestSeller:
        !!product.bestSeller,

      useVariants: true,

      colorGroups:
        groups.length
          ? groups
          : [createEmptyColorGroup()],

      image:
        product.image || '',

      sizesText:
        (product.sizes || []).join(', '),

      stock:
        String(product.stock ?? 0)
    };
  }


  // ==========================================================
  // LEGACY PRODUCT
  // ==========================================================

  return {
    name: product.name || '',
    sku: product.sku || '',
    category: product.category || '',
    categories:
      Array.isArray(product.categories)
      && product.categories.length
        ? product.categories
        : (
            product.category
              ? [product.category]
              : []
          ),
    gender: product.gender || 'Femra',
    season: product.season || 'Verë',

    priceLek: String(
      Math.round(
        (product.price || 0) / 100
      )
    ),

    salePriceLek:
      product.salePrice != null
        ? String(
            Math.round(
              product.salePrice / 100
            )
          )
        : '',

    description:
      product.description || '',

    featured:
      !!product.featured,

    newArrival:
      !!product.newArrival,

    bestSeller:
      !!product.bestSeller,

    useVariants: false,

    colorGroups: [],

    image:
      product.image || '',

    sizesText:
      (product.sizes || []).join(', '),

    stock:
      String(product.stock ?? 10)
  };
};


// ============================================================
// PRODUCT FORM
// ============================================================

const ProductForm = ({
  initial,
  onSave,
  onCancel,
  saving
}) => {

  const [form, setForm] = useState(initial);

  const [uploadingGroup, setUploadingGroup] =
    useState(null);

  const legacyFileInputRef = useRef(null);


  const set = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
  };


  // ==========================================================
  // COLOR GROUPS
  // ==========================================================

  const updateColorGroup = (
    groupId,
    updater
  ) => {

    setForm(prev => ({
      ...prev,

      colorGroups:
        prev.colorGroups.map(group => {

          if (group.id !== groupId) {
            return group;
          }

          return typeof updater === 'function'
            ? updater(group)
            : {
                ...group,
                ...updater
              };
        })
    }));
  };


  const addColorGroup = () => {

    setForm(prev => ({
      ...prev,

      colorGroups: [
        ...prev.colorGroups,
        createEmptyColorGroup()
      ]
    }));
  };


  const removeColorGroup = groupId => {

    setForm(prev => {

      if (prev.colorGroups.length <= 1) {

        toast.error(
          'Produkti duhet të ketë të paktën një ngjyrë'
        );

        return prev;
      }

      return {
        ...prev,

        colorGroups:
          prev.colorGroups.filter(
            group =>
              group.id !== groupId
          )
      };
    });
  };


  // ==========================================================
  // SIZE ROWS
  // ==========================================================

  const addSizeRow = groupId => {

    updateColorGroup(
      groupId,
      group => ({
        ...group,

        sizes: [
          ...group.sizes,
          {
            id: createId(),
            size: '',
            stock: '0'
          }
        ]
      })
    );
  };


  const updateSizeRow = (
    groupId,
    rowId,
    key,
    value
  ) => {

    updateColorGroup(
      groupId,
      group => ({
        ...group,

        sizes:
          group.sizes.map(row => (
            row.id === rowId
              ? {
                  ...row,
                  [key]: value
                }
              : row
          ))
      })
    );
  };


  const removeSizeRow = (
    groupId,
    rowId
  ) => {

    updateColorGroup(
      groupId,
      group => {

        if (group.sizes.length <= 1) {
          return group;
        }

        return {
          ...group,

          sizes:
            group.sizes.filter(
              row => row.id !== rowId
            )
        };
      }
    );
  };


  const applyPreset = (
    groupId,
    presetSizes
  ) => {

    updateColorGroup(
      groupId,
      group => {

        const oldStockMap = {};

        group.sizes.forEach(row => {
          oldStockMap[
            (row.size || '').toLowerCase()
          ] = row.stock;
        });

        return {
          ...group,

          sizes:
            presetSizes.map(size => ({
              id: createId(),

              size,

              stock:
                oldStockMap[
                  size.toLowerCase()
                ] || '0'
            }))
        };
      }
    );
  };


  // ==========================================================
  // MULTIPLE IMAGE UPLOAD
  // ==========================================================

  const handleVariantImagesUpload =
    async (
      groupId,
      event
    ) => {

      const files = Array.from(
        event.target.files || []
      );

      if (!files.length) {
        return;
      }

      setUploadingGroup(groupId);

      try {

        const uploadedUrls = [];

        for (const file of files) {

          const formData =
            new FormData();

          formData.append(
            'file',
            file
          );

          const response =
            await axios.post(
              `${API}/api/upload`,
              formData,
              {
                headers:
                  authHeaders()
              }
            );

          uploadedUrls.push(
            response.data.url
          );
        }

        updateColorGroup(
          groupId,
          group => ({
            ...group,

            images: [
              ...group.images,
              ...uploadedUrls
            ]
          })
        );

        toast.success(
          `${uploadedUrls.length} foto u ngarkuan me sukses`
        );

      } catch (err) {

        toast.error(
          err.response?.data?.detail
          || 'Ngarkimi i fotove dështoi'
        );

      } finally {

        setUploadingGroup(null);

        event.target.value = '';
      }
    };


  const removeGroupImage = (
    groupId,
    imageUrl
  ) => {

    updateColorGroup(
      groupId,
      group => ({
        ...group,

        images:
          group.images.filter(
            url =>
              url !== imageUrl
          )
      })
    );
  };


  // ==========================================================
  // LEGACY IMAGE UPLOAD
  // ==========================================================

  const handleLegacyFileUpload =
    async event => {

      const file =
        event.target.files?.[0];

      if (!file) {
        return;
      }

      setUploadingGroup('legacy');

      try {

        const formData =
          new FormData();

        formData.append(
          'file',
          file
        );

        const response =
          await axios.post(
            `${API}/api/upload`,
            formData,
            {
              headers:
                authHeaders()
            }
          );

        set(
          'image',
          response.data.url
        );

        toast.success(
          'Fotoja u ngarkua me sukses'
        );

      } catch (err) {

        toast.error(
          err.response?.data?.detail
          || 'Ngarkimi i fotos dështoi'
        );

      } finally {

        setUploadingGroup(null);

        if (
          legacyFileInputRef.current
        ) {
          legacyFileInputRef.current.value = '';
        }
      }
    };


  // ==========================================================
  // CONVERT LEGACY TO VARIANTS
  // ==========================================================

  const enableVariants = () => {

    const sizes =
      form.sizesText
        .split(',')
        .map(size => size.trim())
        .filter(Boolean);

    const rows =
      (
        sizes.length
          ? sizes
          : ['S', 'M', 'L', 'XL', 'XXL']
      ).map(size => ({
        id: createId(),
        size,
        stock: '0'
      }));

    setForm(prev => ({
      ...prev,

      useVariants: true,

      colorGroups: [
        {
          id: createId(),

          color: '',

          images:
            prev.image
              ? [prev.image]
              : [],

          sizes: rows
        }
      ]
    }));

    toast.success(
      'Produkti kaloi në sistemin e varianteve. Vendosni ngjyrat dhe stokun për çdo masë.'
    );
  };


  // ==========================================================
  // TOTAL STOCK
  // ==========================================================

  const variantTotalStock =
    form.useVariants
      ? form.colorGroups.reduce(
          (total, group) => (
            total +
            group.sizes.reduce(
              (sum, row) =>
                sum +
                (
                  parseInt(
                    row.stock,
                    10
                  ) || 0
                ),
              0
            )
          ),
          0
        )
      : (
          parseInt(
            form.stock,
            10
          ) || 0
        );


  // ==========================================================
  // SUBMIT
  // ==========================================================

  const handleSubmit = event => {

    event.preventDefault();


    if (!form.name.trim()) {

      toast.error(
        'Vendosni emrin e produktit'
      );

      return;
    }


    if (
      !Array.isArray(form.categories)
      ||
      form.categories.length === 0
    ) {

      toast.error(
        'Zgjidhni të paktën një kategori'
      );

      return;
    }


    const price =
      parseFloat(
        form.priceLek
      );

    if (
      Number.isNaN(price)
      || price < 0
    ) {

      toast.error(
        'Çmimi nuk është i vlefshëm'
      );

      return;
    }


    const salePrice =
      form.salePriceLek.trim()
        ? parseFloat(
            form.salePriceLek
          )
        : null;


    if (
      salePrice !== null
      && (
        Number.isNaN(salePrice)
        || salePrice <= 0
      )
    ) {

      toast.error(
        'Çmimi me ulje nuk është i vlefshëm'
      );

      return;
    }


    if (
      salePrice !== null
      && salePrice >= price
    ) {

      toast.error(
        'Çmimi me ulje duhet të jetë më i vogël se çmimi normal'
      );

      return;
    }


    // ========================================================
    // PRODUCT WITH VARIANTS
    // ========================================================

    if (form.useVariants) {

      if (
        !form.colorGroups.length
      ) {

        toast.error(
          'Shtoni të paktën një ngjyrë'
        );

        return;
      }


      const images = [];
      const variants = [];

      const colorsSeen =
        new Set();


      for (
        const group
        of form.colorGroups
      ) {

        const color =
          group.color.trim();

        if (!color) {

          toast.error(
            'Vendosni emrin e çdo ngjyre'
          );

          return;
        }


        const colorKey =
          color.toLowerCase();

        if (
          colorsSeen.has(
            colorKey
          )
        ) {

          toast.error(
            `Ngjyra "${color}" është përsëritur`
          );

          return;
        }

        colorsSeen.add(
          colorKey
        );


        if (
          !group.images.length
        ) {

          toast.error(
            `Ngjyra "${color}" duhet të ketë të paktën një foto`
          );

          return;
        }


        group.images.forEach(url => {

          images.push({
            color,
            url
          });
        });


        if (
          !group.sizes.length
        ) {

          toast.error(
            `Vendosni të paktën një masë për ngjyrën "${color}"`
          );

          return;
        }


        const sizesSeen =
          new Set();


        for (
          const row
          of group.sizes
        ) {

          const size =
            row.size.trim();

          const sizeKey =
            size.toLowerCase();


          if (
            sizesSeen.has(
              sizeKey
            )
          ) {

            toast.error(
              `Masa "${size || 'Pa masë'}" është përsëritur te ngjyra "${color}"`
            );

            return;
          }

          sizesSeen.add(
            sizeKey
          );


          const stock =
            parseInt(
              row.stock,
              10
            ) || 0;


          if (stock < 0) {

            toast.error(
              'Stoku nuk mund të jetë negativ'
            );

            return;
          }


          variants.push({
            color,
            size,
            stock
          });
        }
      }


      const mainImage =
        images[0]?.url || '';


      onSave({
        name:
          form.name.trim(),

        sku:
          form.sku.trim().toUpperCase(),

        category:
          form.categories[0],

        categories:
          form.categories,

        gender:
          form.gender,

        season:
          form.season,

        price:
          Math.round(
            price * 100
          ),

        salePrice:
          salePrice !== null
            ? Math.round(
                salePrice * 100
              )
            : null,

        image:
          mainImage,

        images,

        variants,

        description:
          form.description,

        featured:
          form.featured,

        newArrival:
          form.newArrival,

        bestSeller:
          form.bestSeller
      });

      return;
    }


    // ========================================================
    // LEGACY PRODUCT
    // ========================================================

    if (!form.image) {

      toast.error(
        'Ju lutemi ngarkoni një foto ose vendosni URL'
      );

      return;
    }


    onSave({
      name:
        form.name.trim(),

      sku:
        form.sku.trim().toUpperCase(),

      category:
        form.categories[0],

      categories:
        form.categories,

      gender:
        form.gender,

      season:
        form.season,

      price:
        Math.round(
          price * 100
        ),

      salePrice:
        salePrice !== null
          ? Math.round(
              salePrice * 100
            )
          : null,

      image:
        form.image,

      description:
        form.description,

      sizes:
        form.sizesText
          .split(',')
          .map(
            size =>
              size.trim()
          )
          .filter(Boolean),

      stock:
        parseInt(
          form.stock,
          10
        ) || 0,

      featured:
        form.featured,

      newArrival:
        form.newArrival,

      bestSeller:
        form.bestSeller
    });
  };


  return (

    <form
      onSubmit={handleSubmit}
      className="space-y-6"
    >

      {/* BASIC INFO */}

      <div>

        <Label>
          Emri i Produktit *
        </Label>

        <Input
          required

          value={form.name}

          onChange={
            e =>
              set(
                'name',
                e.target.value
              )
          }

          className="mt-1 rounded-none"
        />

      </div>


      <div>

        <Label>
          SKU / Kodi i Produktit
        </Label>

        <Input
          value={form.sku}
          onChange={
            e =>
              set(
                'sku',
                e.target.value.toUpperCase()
              )
          }
          className="mt-1 rounded-none uppercase"
          placeholder="P.sh. ATL001"
        />

        <p className="text-xs text-gray-500 mt-1">
          SKU përdoret për importin masiv dhe lidhjen automatike të fotove ZIP.
        </p>

      </div>


      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        <div>

          <Label>
            Kategoritë *
          </Label>

          <p className="text-xs text-gray-500 mt-1 mb-3">
            Mund të zgjidhni një ose disa kategori për të njëjtin produkt.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 border border-gray-200 p-3">

            {KNOWN_CATEGORIES.map(category => {

              const checked =
                Array.isArray(form.categories)
                &&
                form.categories.some(
                  item =>
                    item.toLowerCase()
                    === category.toLowerCase()
                );

              return (

                <label
                  key={category}
                  className={`
                    flex
                    items-center
                    gap-2
                    px-3
                    py-2
                    border
                    cursor-pointer
                    text-sm
                    transition-colors

                    ${
                      checked
                        ? 'border-[#c8953d] bg-[#fff8ea] text-gray-900'
                        : 'border-gray-200 bg-white hover:border-gray-400'
                    }
                  `}
                >

                  <Checkbox
                    checked={checked}
                    onCheckedChange={
                      value => {

                        setForm(prev => {

                          const current =
                            Array.isArray(prev.categories)
                              ? prev.categories
                              : [];

                          const next =
                            value
                              ? [
                                  ...current.filter(
                                    item =>
                                      item.toLowerCase()
                                      !== category.toLowerCase()
                                  ),
                                  category
                                ]
                              : current.filter(
                                  item =>
                                    item.toLowerCase()
                                    !== category.toLowerCase()
                                );

                          return {
                            ...prev,
                            categories: next,
                            category: next[0] || ''
                          };
                        });
                      }
                    }
                  />

                  <span>
                    {category}
                  </span>

                </label>
              );
            })}

          </div>

          {form.categories?.length > 0 && (

            <p className="text-xs text-gray-500 mt-2">
              Të zgjedhura: {form.categories.join(', ')}
            </p>

          )}

        </div>


        <div>

          <Label>
            Çmimi (Lek) *
          </Label>

          <Input
            required
            type="number"
            min="0"
            step="1"

            value={form.priceLek}

            onChange={
              e =>
                set(
                  'priceLek',
                  e.target.value
                )
            }

            className="mt-1 rounded-none"
          />

        </div>

      </div>


      <div>

        <Label>
          Çmimi me Ulje (Lek)
        </Label>

        <Input
          type="number"
          min="0"
          step="1"
          value={form.salePriceLek}
          onChange={
            e =>
              set(
                'salePriceLek',
                e.target.value
              )
          }
          className="mt-1 rounded-none"
          placeholder="Lëreni bosh nëse produkti nuk ka ulje"
        />

        <p className="text-xs text-gray-500 mt-1">
          Kur vendoset, çmimi normal shfaqet me vijë dhe klienti paguan çmimin me ulje.
        </p>

      </div>


      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        <div>

          <Label>
            Gjinia *
          </Label>

          <Select
            value={form.gender}

            onValueChange={
              value =>
                set(
                  'gender',
                  value
                )
            }
          >

            <SelectTrigger className="mt-1 rounded-none">

              <SelectValue />

            </SelectTrigger>

            <SelectContent>

              <SelectItem value="Femra">
                Femra
              </SelectItem>

              <SelectItem value="Meshkuj">
                Meshkuj
              </SelectItem>

            </SelectContent>

          </Select>

        </div>


        <div>

          <Label>
            Stina *
          </Label>

          <Select
            value={form.season}

            onValueChange={
              value =>
                set(
                  'season',
                  value
                )
            }
          >

            <SelectTrigger className="mt-1 rounded-none">

              <SelectValue />

            </SelectTrigger>

            <SelectContent>

              <SelectItem value="Verë">
                Verë
              </SelectItem>

              <SelectItem value="Dimër">
                Dimër
              </SelectItem>

              <SelectItem value="Të gjitha">
                Të gjitha
              </SelectItem>

            </SelectContent>

          </Select>

        </div>

      </div>


      {/* =====================================================
          VARIANT SYSTEM
      ====================================================== */}

      {form.useVariants ? (

        <div className="space-y-5">

          <div className="flex items-center justify-between border-b pb-3">

            <div>

              <h3 className="font-semibold text-gray-900">
                Ngjyrat, Masat dhe Stoku
              </h3>

              <p className="text-xs text-gray-500 mt-1">
                Stoku total: {variantTotalStock}
              </p>

            </div>


            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addColorGroup}
              className="rounded-none"
            >

              <Plus className="h-4 w-4 mr-1" />

              Shto Ngjyrë

            </Button>

          </div>


          {form.colorGroups.map(
            (
              group,
              groupIndex
            ) => (

              <Card
                key={group.id}
                className="border border-gray-200 shadow-none"
              >

                <CardContent className="p-5 space-y-5">

                  {/* COLOR */}

                  <div className="flex items-end gap-3">

                    <div className="flex-grow">

                      <Label>
                        Ngjyra #{groupIndex + 1} *
                      </Label>

                      <Input
                        required

                        value={group.color}

                        onChange={
                          e =>
                            updateColorGroup(
                              group.id,
                              {
                                color:
                                  e.target.value
                              }
                            )
                        }

                        placeholder="p.sh. E zezë"

                        className="mt-1 rounded-none"
                      />

                    </div>


                    <Button
                      type="button"

                      variant="outline"

                      onClick={
                        () =>
                          removeColorGroup(
                            group.id
                          )
                      }

                      className="rounded-none text-red-600 border-red-300"
                    >

                      <Trash2 className="h-4 w-4" />

                    </Button>

                  </div>


                  {/* IMAGES */}

                  <div>

                    <Label>
                      Fotot për këtë ngjyrë *
                    </Label>

                    <div className="mt-2">

                      <label
                        className="
                          inline-flex
                          items-center
                          px-4
                          py-2
                          border
                          border-gray-300
                          cursor-pointer
                          hover:border-gray-900
                          text-sm
                        "
                      >

                        <Upload className="h-4 w-4 mr-2" />

                        {
                          uploadingGroup
                            === group.id

                            ? 'Duke ngarkuar...'

                            : 'Ngarko Foto'
                        }

                        <input
                          type="file"

                          multiple

                          accept="
                            image/jpeg,
                            image/png,
                            image/webp,
                            image/gif
                          "

                          disabled={
                            uploadingGroup
                            === group.id
                          }

                          onChange={
                            e =>
                              handleVariantImagesUpload(
                                group.id,
                                e
                              )
                          }

                          className="hidden"
                        />

                      </label>

                    </div>


                    {group.images.length > 0 && (

                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-3">

                        {group.images.map(
                          (
                            url,
                            index
                          ) => (

                            <div
                              key={`${url}-${index}`}
                              className="relative group"
                            >

                              <img
                                src={imgUrl(url)}

                                alt={
                                  `${group.color} ${index + 1}`
                                }

                                className="
                                  w-full
                                  aspect-square
                                  object-cover
                                  border
                                "
                              />


                              <button
                                type="button"

                                onClick={
                                  () =>
                                    removeGroupImage(
                                      group.id,
                                      url
                                    )
                                }

                                className="
                                  absolute
                                  -top-2
                                  -right-2
                                  bg-red-600
                                  text-white
                                  rounded-full
                                  w-6
                                  h-6
                                  items-center
                                  justify-center
                                  hidden
                                  group-hover:flex
                                "
                              >

                                <X className="h-3 w-3" />

                              </button>

                            </div>

                          )
                        )}

                      </div>

                    )}

                  </div>


                  {/* SIZE PRESETS */}

                  <div>

                    <div className="flex flex-wrap gap-2">

                      {SIZE_PRESETS.map(
                        preset => (

                          <button
                            key={preset.label}

                            type="button"

                            onClick={
                              () =>
                                applyPreset(
                                  group.id,
                                  preset.sizes
                                )
                            }

                            className="
                              text-xs
                              border
                              border-gray-300
                              px-3
                              py-1.5
                              hover:border-gray-900
                            "
                          >

                            {preset.label}

                          </button>

                        )
                      )}

                    </div>

                  </div>


                  {/* SIZES + STOCK */}

                  <div>

                    <div className="grid grid-cols-[1fr_1fr_40px] gap-2 mb-2">

                      <p className="text-xs font-semibold text-gray-500 uppercase">
                        Masa
                      </p>

                      <p className="text-xs font-semibold text-gray-500 uppercase">
                        Stoku
                      </p>

                    </div>


                    <div className="space-y-2">

                      {group.sizes.map(
                        row => (

                          <div
                            key={row.id}

                            className="
                              grid
                              grid-cols-[1fr_1fr_40px]
                              gap-2
                            "
                          >

                            <Input
                              value={row.size}

                              onChange={
                                e =>
                                  updateSizeRow(
                                    group.id,
                                    row.id,
                                    'size',
                                    e.target.value
                                  )
                              }

                              placeholder="p.sh. M"

                              className="rounded-none"
                            />


                            <Input
                              type="number"
                              min="0"
                              step="1"

                              value={row.stock}

                              onChange={
                                e =>
                                  updateSizeRow(
                                    group.id,
                                    row.id,
                                    'stock',
                                    e.target.value
                                  )
                              }

                              className="rounded-none"
                            />


                            <button
                              type="button"

                              onClick={
                                () =>
                                  removeSizeRow(
                                    group.id,
                                    row.id
                                  )
                              }

                              className="
                                border
                                border-gray-300
                                flex
                                items-center
                                justify-center
                                hover:border-red-500
                                hover:text-red-600
                              "
                            >

                              <X className="h-4 w-4" />

                            </button>

                          </div>

                        )
                      )}

                    </div>


                    <Button
                      type="button"

                      variant="outline"

                      size="sm"

                      onClick={
                        () =>
                          addSizeRow(
                            group.id
                          )
                      }

                      className="rounded-none mt-3"
                    >

                      <Plus className="h-4 w-4 mr-1" />

                      Shto Masë

                    </Button>

                  </div>


                  <div className="bg-gray-50 px-3 py-2 text-sm">

                    Stoku për
                    {' '}
                    <strong>
                      {group.color || 'këtë ngjyrë'}
                    </strong>
                    :
                    {' '}

                    <strong>

                      {
                        group.sizes.reduce(
                          (
                            sum,
                            row
                          ) =>
                            sum +
                            (
                              parseInt(
                                row.stock,
                                10
                              ) || 0
                            ),
                          0
                        )
                      }

                    </strong>

                  </div>

                </CardContent>

              </Card>

            )
          )}

        </div>

      ) : (

        /* ===================================================
           LEGACY PRODUCT
        ==================================================== */

        <div className="space-y-5 border border-amber-200 bg-amber-50 p-5">

          <div>

            <p className="font-semibold text-amber-900">
              Produkt i sistemit të vjetër
            </p>

            <p className="text-sm text-amber-700 mt-1">
              Ky produkt përdor stok total dhe nuk ka stok të ndarë sipas ngjyrës/masës.
            </p>

          </div>


          <Button
            type="button"

            onClick={enableVariants}

            className="
              bg-gray-900
              hover:bg-gray-800
              text-white
              rounded-none
            "
          >

            Kalo në Ngjyra / Masa / Stok

          </Button>


          <div>

            <Label>
              Sasia Totale në Stok
            </Label>

            <Input
              type="number"
              min="0"

              value={form.stock}

              onChange={
                e =>
                  set(
                    'stock',
                    e.target.value
                  )
              }

              className="mt-1 rounded-none"
            />

          </div>


          <div>

            <Label>
              Masat
            </Label>

            <Input
              value={form.sizesText}

              onChange={
                e =>
                  set(
                    'sizesText',
                    e.target.value
                  )
              }

              className="mt-1 rounded-none"
            />

          </div>


          <div>

            <Label>
              Fotoja
            </Label>

            <div className="flex items-center gap-3 mt-2">

              <input
                ref={legacyFileInputRef}

                type="file"

                accept="
                  image/jpeg,
                  image/png,
                  image/webp,
                  image/gif
                "

                onChange={
                  handleLegacyFileUpload
                }

                className="hidden"
              />


              <Button
                type="button"

                variant="outline"

                disabled={
                  uploadingGroup
                  === 'legacy'
                }

                onClick={
                  () =>
                    legacyFileInputRef
                      .current
                      ?.click()
                }

                className="rounded-none"
              >

                <Upload className="h-4 w-4 mr-2" />

                Ngarko Foto

              </Button>


              {form.image && (

                <img
                  src={imgUrl(form.image)}

                  alt="Preview"

                  className="
                    h-16
                    w-14
                    object-cover
                    border
                  "
                />

              )}

            </div>


            <Input
              type="text"

              value={form.image}

              onChange={
                e =>
                  set(
                    'image',
                    e.target.value
                  )
              }

              placeholder="URL e fotos"

              className="mt-2 rounded-none text-xs"
            />

          </div>

        </div>

      )}


      {/* DESCRIPTION */}

      <div>

        <Label>
          Përshkrimi
        </Label>

        <Textarea
          value={form.description}

          onChange={
            e =>
              set(
                'description',
                e.target.value
              )
          }

          className="mt-1 rounded-none"

          rows={3}
        />

      </div>


      {/* FLAGS */}

      <div className="flex flex-wrap gap-6 pt-2">

        {[
          ['featured', 'E Zgjedhur'],
          ['newArrival', 'E Re'],
          ['bestSeller', 'Më e Shitur']
        ].map(
          ([key, label]) => (

            <label
              key={key}

              className="
                flex
                items-center
                gap-2
                text-sm
                cursor-pointer
              "
            >

              <Checkbox
                checked={form[key]}

                onCheckedChange={
                  value =>
                    set(
                      key,
                      !!value
                    )
                }
              />

              {label}

            </label>

          )
        )}

      </div>


      {/* FOOTER */}

      <DialogFooter className="pt-4">

        <Button
          type="button"

          variant="outline"

          onClick={onCancel}

          className="rounded-none"
        >

          Anulo

        </Button>


        <Button
          type="submit"

          disabled={
            saving
            || uploadingGroup !== null
          }

          className="
            bg-gray-900
            hover:bg-gray-800
            text-white
            rounded-none
          "
        >

          {
            saving
              ? 'Duke ruajtur...'
              : 'Ruaj Produktin'
          }

        </Button>

      </DialogFooter>

    </form>
  );
};


// ============================================================
// CHANGE PASSWORD
// ============================================================

const ChangePasswordDialog = ({
  open,
  onOpenChange
}) => {

  const [current, setCurrent] =
    useState('');

  const [newPass, setNewPass] =
    useState('');

  const [confirm, setConfirm] =
    useState('');

  const [saving, setSaving] =
    useState(false);


  const handleSubmit = async event => {

    event.preventDefault();


    if (
      newPass !== confirm
    ) {

      toast.error(
        'Fjalëkalimet e reja nuk përputhen'
      );

      return;
    }


    setSaving(true);


    try {

      await axios.post(
        `${API}/api/auth/change-password`,

        {
          current_password:
            current,

          new_password:
            newPass
        },

        {
          headers:
            authHeaders()
        }
      );


      toast.success(
        'Fjalëkalimi u ndryshua me sukses!'
      );


      setCurrent('');
      setNewPass('');
      setConfirm('');


      onOpenChange(false);

    } catch (err) {

      toast.error(
        typeof err.response?.data?.detail
          === 'string'

          ? err.response.data.detail

          : 'Gabim gjatë ndryshimit të fjalëkalimit'
      );

    } finally {

      setSaving(false);
    }
  };


  return (

    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >

      <DialogContent className="max-w-md rounded-none">

        <DialogHeader>

          <DialogTitle className="font-serif">
            Ndrysho Fjalëkalimin
          </DialogTitle>

        </DialogHeader>


        <form
          onSubmit={handleSubmit}
          className="space-y-4"
        >

          <div>

            <Label>
              Fjalëkalimi Aktual *
            </Label>

            <Input
              required
              type="password"

              value={current}

              onChange={
                e =>
                  setCurrent(
                    e.target.value
                  )
              }

              className="mt-1 rounded-none"
            />

          </div>


          <div>

            <Label>
              Fjalëkalimi i Ri * (min. 8 karaktere)
            </Label>

            <Input
              required
              type="password"

              minLength={8}

              value={newPass}

              onChange={
                e =>
                  setNewPass(
                    e.target.value
                  )
              }

              className="mt-1 rounded-none"
            />

          </div>


          <div>

            <Label>
              Konfirmo Fjalëkalimin e Ri *
            </Label>

            <Input
              required
              type="password"

              minLength={8}

              value={confirm}

              onChange={
                e =>
                  setConfirm(
                    e.target.value
                  )
              }

              className="mt-1 rounded-none"
            />

          </div>


          <DialogFooter className="pt-2">

            <Button
              type="button"

              variant="outline"

              onClick={
                () =>
                  onOpenChange(
                    false
                  )
              }

              className="rounded-none"
            >

              Anulo

            </Button>


            <Button
              type="submit"

              disabled={saving}

              className="
                bg-gray-900
                hover:bg-gray-800
                text-white
                rounded-none
              "
            >

              {
                saving
                  ? 'Duke ruajtur...'
                  : 'Ndrysho Fjalëkalimin'
              }

            </Button>

          </DialogFooter>

        </form>

      </DialogContent>

    </Dialog>
  );
};


// ============================================================
// STORE INVENTORY / SALES
// ============================================================

const lek = value =>
  `${Math.round(Number(value || 0) / 100).toLocaleString('sq-AL')} Lek`;

const tiranaDayKey = value =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Tirane',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date(value));

const StoreInventoryTab = ({ sales, products, reload }) => {
  const [productSearch, setProductSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedVariantIndex, setSelectedVariantIndex] = useState('');
  const [legacySize, setLegacySize] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [amountLek, setAmountLek] = useState('');
  const [savingSale, setSavingSale] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [historyDate, setHistoryDate] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [analysisMode, setAnalysisMode] = useState('');

  const normalizeSearch = value => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
  const productTokens = normalizeSearch(productSearch).split(/\s+/).filter(Boolean);
  const availableProducts = products.filter(product => (
    Number(product.stock || 0) > 0
    && productTokens.every(token => normalizeSearch([
      product.name,
      product.sku,
      product.category,
      ...(product.categories || []),
      ...(product.variants || []).flatMap(variant => [variant.color, variant.size])
    ].filter(Boolean).join(' ')).includes(token))
  ));
  const selectedProduct = products.find(product => product.id === selectedProductId);
  const variants = selectedProduct?.variants || [];
  const selectedVariant = selectedVariantIndex !== ''
    ? variants[Number(selectedVariantIndex)]
    : null;
  const unitPrice = selectedProduct
    ? (
        Number(selectedProduct.salePrice || 0) > 0
        && Number(selectedProduct.salePrice) < Number(selectedProduct.price || 0)
          ? Number(selectedProduct.salePrice)
          : Number(selectedProduct.price || 0)
      )
    : 0;
  const selectedStock = selectedVariant
    ? Number(selectedVariant.stock || 0)
    : Number(selectedProduct?.stock || 0);

  const productColors = [...new Set(
    variants.map(variant => variant.color || '').filter(Boolean)
  )];

  const selectedImage = (() => {
    if (!selectedProduct) return '';
    const colorImage = (selectedProduct.images || []).find(
      image => normalizeSearch(image.color) === normalizeSearch(selectedColor)
    );
    return colorImage?.url
      || selectedProduct.image
      || selectedProduct.images?.[0]?.url
      || '';
  })();

  const selectProduct = productId => {
    const product = products.find(item => item.id === productId);
    const productVariants = product?.variants || [];
    const firstAvailableIndex = productVariants.findIndex(
      item => Number(item.stock || 0) > 0
    );
    const price = product
      ? (
          Number(product.salePrice || 0) > 0
          && Number(product.salePrice) < Number(product.price || 0)
            ? Number(product.salePrice)
            : Number(product.price || 0)
        )
      : 0;

    setSelectedProductId(productId);
    setSelectedColor(productVariants[firstAvailableIndex]?.color || '');
    setSelectedVariantIndex(firstAvailableIndex >= 0 ? String(firstAvailableIndex) : '');
    setLegacySize(product?.sizes?.[0] || '');
    setQuantity('1');
    setAmountLek(price ? String(Math.round(price / 100)) : '');
  };

  const selectColor = color => {
    const firstIndex = variants.findIndex(
      variant => normalizeSearch(variant.color) === normalizeSearch(color)
        && Number(variant.stock || 0) > 0
    );
    setSelectedColor(color);
    setSelectedVariantIndex(firstIndex >= 0 ? String(firstIndex) : '');
  };

  const changeQuantity = value => {
    const nextQuantity = Math.max(1, Number(value || 1));
    setQuantity(String(nextQuantity));
    if (unitPrice > 0) {
      setAmountLek(String(Math.round(unitPrice * nextQuantity / 100)));
    }
  };

  const savePhysicalSale = async event => {
    event.preventDefault();
    if (!selectedProduct) {
      toast.error('Zgjidh produktin');
      return;
    }
    if (variants.length > 0 && !selectedVariant) {
      toast.error('Zgjidh ngjyrën dhe masën');
      return;
    }
    const amount = Math.round(Number(amountLek || 0) * 100);
    if (amount <= 0) {
      toast.error('Vendos vlerën reale të shitjes');
      return;
    }

    setSavingSale(true);
    try {
      await axios.post(
        `${API}/api/store-sales/physical`,
        {
          product_id: selectedProduct.id,
          color: selectedVariant?.color || '',
          size: selectedVariant?.size || legacySize || '',
          quantity: Number(quantity || 1),
          amount
        },
        { headers: authHeaders() }
      );
      toast.success('Shitja fizike u regjistrua dhe stoku u përditësua');
      setSelectedProductId('');
      setSelectedVariantIndex('');
      setLegacySize('');
      setQuantity('1');
      setAmountLek('');
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Shitja nuk u regjistrua');
    } finally {
      setSavingSale(false);
    }
  };

  const deleteSale = async sale => {
    const sourceText = sale.source === 'online'
      ? `porosinë #${sale.order_id}`
      : 'shitjen fizike';
    if (!window.confirm(
      `Ta fshij ${sourceText}?\n\nShitja do të hiqet plotësisht dhe stoku do të rikthehet.`
    )) return;

    try {
      await axios.delete(
        `${API}/api/store-sales/${sale.id}`,
        { headers: authHeaders() }
      );
      toast.success('Shitja u fshi dhe stoku u rikthye');
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Shitja nuk u fshi');
    }
  };

  const today = tiranaDayKey(new Date());
  const month = today.slice(0, 7);
  const year = today.slice(0, 4);
  const totalFor = rows => rows.reduce(
    (sum, sale) => sum + Number(sale.amount || 0), 0
  );
  const todaySales = sales.filter(sale => tiranaDayKey(sale.created_at) === today);
  const monthSales = sales.filter(
    sale => tiranaDayKey(sale.created_at).slice(0, 7) === month
  );
  const yearSales = sales.filter(
    sale => tiranaDayKey(sale.created_at).slice(0, 4) === year
  );

  const productPerformance = products.map(product => {
    let sold = 0;
    let revenue = 0;
    sales.forEach(sale => {
      (sale.items || []).forEach(item => {
        if (item.product_id !== product.id) return;
        const itemQuantity = Number(item.quantity || 0);
        sold += itemQuantity;
        revenue += sale.source === 'physical'
          ? Number(sale.amount || 0)
          : Number(item.price || 0) * itemQuantity;
      });
    });
    return {
      id: product.id,
      name: product.name,
      sku: product.sku || '',
      image: product.image || product.images?.[0]?.url || '',
      sold,
      revenue,
      stock: Number(product.stock || 0)
    };
  });
  const mostSold = [...productPerformance].sort(
    (a, b) => b.sold - a.sold || b.revenue - a.revenue
  );
  const leastSold = [...productPerformance].sort(
    (a, b) => a.sold - b.sold || a.revenue - b.revenue
  );
  const analysisRows = analysisMode === 'most' ? mostSold : leastSold;

  const filteredSales = sales.filter(sale => {
    const query = historySearch.trim().toLowerCase();
    const searchable = [
      sale.order_id,
      sale.created_by,
      ...(sale.items || []).flatMap(item => [item.name, item.color, item.size])
    ].filter(Boolean).join(' ').toLowerCase();
    return (
      (!query || searchable.includes(query))
      && (sourceFilter === 'all' || sale.source === sourceFilter)
      && (!historyDate || tiranaDayKey(sale.created_at) === historyDate)
    );
  });
  const pageSize = 50;
  const pages = Math.max(1, Math.ceil(filteredSales.length / pageSize));
  const currentPage = Math.min(historyPage, pages);
  const pagedSales = filteredSales.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Xhiroja sot', value: totalFor(todaySales), rows: todaySales },
          { label: 'Xhiroja këtë muaj', value: totalFor(monthSales), rows: monthSales },
          { label: 'Xhiroja këtë vit', value: totalFor(yearSales), rows: yearSales },
          {
            label: 'Produkte të shitura këtë vit',
            value: yearSales.reduce(
              (sum, sale) => sum + Number(sale.quantity || 0), 0
            ),
            count: true,
            rows: yearSales
          }
        ].map(item => (
          <Card key={item.label} className="border-none shadow-sm">
            <CardContent className="p-4 sm:p-5">
              <p className="text-xs sm:text-sm text-gray-500">{item.label}</p>
              <p className="mt-1 text-xl sm:text-2xl font-bold text-gray-900">
                {item.count ? item.value : lek(item.value)}
              </p>
              <div className="mt-2 space-y-1 border-t pt-2 text-xs text-gray-600">
                {['physical', 'online'].map(source => {
                  const sourceRows = item.rows.filter(sale => sale.source === source);
                  const sourceValue = item.count
                    ? sourceRows.reduce(
                        (sum, sale) => sum + Number(sale.quantity || 0), 0
                      )
                    : lek(totalFor(sourceRows));
                  return (
                    <div className="flex justify-between gap-2" key={source}>
                      <span>{source === 'physical' ? 'Në dyqan' : 'Online'}</span>
                      <b>{sourceValue}</b>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="border-none shadow-sm lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Store className="h-5 w-5 text-[#c8953d]" />
              <h2 className="text-lg font-bold">Regjistro shitje në dyqan</h2>
            </div>
            <form onSubmit={savePhysicalSale} className="space-y-4">
              <div>
                <Label>Kërko produkt</Label>
                <Input
                  value={productSearch}
                  onChange={event => setProductSearch(event.target.value)}
                  placeholder="Emri, SKU ose kategoria..."
                  className="mt-1 rounded-none"
                />
              </div>
              <div>
                <Label>Rezultatet ({availableProducts.length})</Label>
                <div className="mt-2 grid max-h-[430px] grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3">
                  {availableProducts.map(product => {
                    const productImage = product.image || product.images?.[0]?.url || '';
                    const price = Number(product.salePrice || 0) > 0
                      && Number(product.salePrice) < Number(product.price || 0)
                        ? Number(product.salePrice)
                        : Number(product.price || 0);
                    return (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => selectProduct(product.id)}
                        className={`overflow-hidden border bg-white text-left transition ${
                          selectedProductId === product.id
                            ? 'border-[#c8953d] ring-2 ring-[#c8953d]/30'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <img
                          src={imgUrl(productImage)}
                          alt={product.name}
                          className="h-32 w-full bg-gray-100 object-cover sm:h-40"
                        />
                        <div className="p-2.5">
                          <b className="block line-clamp-2 text-sm">{product.name}</b>
                          <p className="mt-1 text-xs text-gray-500">
                            {product.sku || 'Pa SKU'} • {product.stock} copë
                          </p>
                          <p className="mt-1 font-bold text-gray-900">{lek(price)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {availableProducts.length === 0 && (
                  <p className="mt-2 border bg-gray-50 p-4 text-center text-sm text-gray-500">
                    Nuk u gjet produkt me këtë kërkim.
                  </p>
                )}
              </div>

              {variants.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
                  {selectedImage && (
                    <img
                      src={imgUrl(selectedImage)}
                      alt={selectedProduct?.name || ''}
                      className="h-44 w-full border bg-gray-100 object-cover sm:h-48"
                    />
                  )}
                  <div className="space-y-4">
                    {productColors.length > 0 && (
                      <div>
                        <Label>Ngjyra *</Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {productColors.map(color => {
                            const colorStock = variants
                              .filter(variant => normalizeSearch(variant.color) === normalizeSearch(color))
                              .reduce((sum, variant) => sum + Number(variant.stock || 0), 0);
                            return (
                              <Button
                                key={color}
                                type="button"
                                variant={selectedColor === color ? 'default' : 'outline'}
                                disabled={colorStock <= 0}
                                onClick={() => selectColor(color)}
                                className="rounded-none"
                              >
                                {color} ({colorStock})
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <Label>Masa *</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {variants.map((variant, index) => {
                          if (productColors.length > 0
                            && normalizeSearch(variant.color) !== normalizeSearch(selectedColor)) {
                            return null;
                          }
                          return (
                            <Button
                              key={`${variant.color}-${variant.size}-${index}`}
                              type="button"
                              variant={selectedVariantIndex === String(index) ? 'default' : 'outline'}
                              disabled={Number(variant.stock || 0) <= 0}
                              onClick={() => setSelectedVariantIndex(String(index))}
                              className="min-w-14 rounded-none"
                            >
                              {variant.size || 'Pa masë'} ({variant.stock})
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (selectedProduct?.sizes || []).length > 0 ? (
                <div>
                  <Label>Masa</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(selectedProduct.sizes || []).map(size => (
                      <Button key={size} type="button"
                        variant={legacySize === size ? 'default' : 'outline'}
                        onClick={() => setLegacySize(size)} className="rounded-none">
                        {size || 'Pa masë'}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Sasia *</Label>
                  <Input
                    type="number"
                    min="1"
                    max={Math.max(1, selectedStock)}
                    value={quantity}
                    onChange={event => changeQuantity(event.target.value)}
                    className="mt-1 rounded-none"
                  />
                </div>
                <div>
                  <Label>Vlera reale e shitjes (Lek) *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={amountLek}
                    onChange={event => setAmountLek(event.target.value)}
                    placeholder="p.sh. 2500"
                    className="mt-1 rounded-none"
                  />
                </div>
              </div>
              {selectedProduct && (
                <div className="border bg-gray-50 p-3 text-sm">
                  Çmimi online: <b>{lek(unitPrice)}</b> për copë
                  {' • '}Në stok: <b>{selectedStock} copë</b>
                </div>
              )}
              <Button
                type="submit"
                disabled={savingSale || !selectedProductId}
                className="w-full rounded-none bg-[#1f2937]"
              >
                <ReceiptText className="h-4 w-4 mr-2" />
                {savingSale ? 'Duke regjistruar...' : 'Shëno si të shitur'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <WalletCards className="h-5 w-5 text-green-700" />
              <h2 className="font-bold">Ndarja e xhiros këtë muaj</h2>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <span>Në dyqan</span>
                <b>{lek(totalFor(monthSales.filter(sale => sale.source === 'physical')))}</b>
              </div>
              <div className="flex justify-between gap-3">
                <span>Online</span>
                <b>{lek(totalFor(monthSales.filter(sale => sale.source === 'online')))}</b>
              </div>
              <div className="flex justify-between gap-3 border-t pt-3 text-base">
                <span>Totali</span>
                <b className="text-green-700">{lek(totalFor(monthSales))}</b>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-none shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-bold">Analiza e produkteve</h2>
              <p className="text-sm text-gray-500">
                Kliko për të parë çfarë shitet më shumë ose më pak.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button"
                variant={analysisMode === 'most' ? 'default' : 'outline'}
                onClick={() => setAnalysisMode(analysisMode === 'most' ? '' : 'most')}
                className="rounded-none">
                Më të shiturat
              </Button>
              <Button type="button"
                variant={analysisMode === 'least' ? 'default' : 'outline'}
                onClick={() => setAnalysisMode(analysisMode === 'least' ? '' : 'least')}
                className="rounded-none">
                Më pak të shiturat
              </Button>
            </div>
          </div>

          {analysisMode && (
            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {analysisRows.slice(0, 30).map((product, index) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => {
                    selectProduct(product.id);
                    setProductSearch(product.name);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="flex items-center gap-3 border bg-white p-2 text-left hover:border-[#c8953d]"
                >
                  <span className="w-6 shrink-0 text-center text-sm font-bold text-gray-400">
                    {index + 1}
                  </span>
                  <img src={imgUrl(product.image)} alt=""
                    className="h-14 w-12 shrink-0 bg-gray-100 object-cover" />
                  <span className="min-w-0 flex-1">
                    <b className="block truncate text-sm">{product.name}</b>
                    <span className="block text-xs text-gray-500">
                      Shitur: {product.sold} • Stok: {product.stock}
                    </span>
                    <span className="block text-xs font-semibold text-green-700">
                      Xhiro: {lek(product.revenue)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-none shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4 lg:items-end">
            <div>
              <Label>Kërko në historik</Label>
              <Input
                value={historySearch}
                onChange={event => { setHistorySearch(event.target.value); setHistoryPage(1); }}
                placeholder="Produkt, masë, ngjyrë, porosi..."
                className="mt-1 rounded-none"
              />
            </div>
            <div>
              <Label>Burimi</Label>
              <select
                value={sourceFilter}
                onChange={event => { setSourceFilter(event.target.value); setHistoryPage(1); }}
                className="mt-1 h-10 w-full border border-gray-300 bg-white px-3"
              >
                <option value="all">Të gjitha</option>
                <option value="physical">Në dyqan</option>
                <option value="online">Online</option>
              </select>
            </div>
            <div>
              <Label>Data</Label>
              <Input
                type="date"
                value={historyDate}
                onChange={event => { setHistoryDate(event.target.value); setHistoryPage(1); }}
                className="mt-1 rounded-none"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setHistorySearch(''); setSourceFilter('all');
                setHistoryDate(''); setHistoryPage(1);
              }}
              className="rounded-none"
            >
              Pastro filtrat
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {pagedSales.length === 0 ? (
          <Card className="border-none shadow-sm">
            <CardContent className="p-10 text-center text-gray-500">
              Nuk ka shitje në historik.
            </CardContent>
          </Card>
        ) : pagedSales.map(sale => (
          <Card key={sale.id} className="border-none shadow-sm">
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={sale.source === 'online'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-amber-100 text-amber-800'}>
                      {sale.source === 'online' ? 'Online' : 'Në dyqan'}
                    </Badge>
                    {sale.order_id && <b>#{sale.order_id}</b>}
                    <span className="text-sm text-gray-500">
                      {new Date(sale.created_at).toLocaleString('sq-AL')}
                    </span>
                  </div>
                  <div className="mt-3 space-y-2">
                    {(sale.items || []).map((item, index) => (
                      <div key={`${sale.id}-${index}`} className="text-sm">
                        <b>{item.name}</b>
                        {item.color ? ` • ${item.color}` : ''}
                        {item.size ? ` • Masa ${item.size}` : ''}
                        {` • ${item.quantity} copë`}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex shrink-0 items-center justify-between gap-3 sm:flex-col sm:items-end">
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Vlera e arkëtuar</p>
                    <b className="text-lg text-green-700">{lek(sale.amount)}</b>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => deleteSale(sale)}
                    className="rounded-none border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />Fshi
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" disabled={currentPage <= 1}
            onClick={() => setHistoryPage(currentPage - 1)} className="rounded-none">
            E mëparshme
          </Button>
          <span className="text-sm font-semibold">Faqja {currentPage} nga {pages}</span>
          <Button variant="outline" disabled={currentPage >= pages}
            onClick={() => setHistoryPage(currentPage + 1)} className="rounded-none">
            Tjetra
          </Button>
        </div>
      )}
    </div>
  );
};


// ============================================================
// ADMIN
// ============================================================

export default function Admin() {

  const navigate =
    useNavigate();


  const [orders, setOrders] =
    useState([]);

  const [products, setProducts] =
    useState([]);

  const [storeSales, setStoreSales] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [dialogOpen, setDialogOpen] =
    useState(false);

  const [
    passDialogOpen,
    setPassDialogOpen
  ] = useState(false);

  const [
    editingProduct,
    setEditingProduct
  ] = useState(null);

  const [saving, setSaving] =
    useState(false);

  const [productSearch, setProductSearch] =
    useState('');

  const [orderSearch, setOrderSearch] =
    useState('');

  const [orderStatusFilter, setOrderStatusFilter] =
    useState('all');

  const [orderDateFrom, setOrderDateFrom] =
    useState('');

  const [orderDateTo, setOrderDateTo] =
    useState('');

  const [importingExcel, setImportingExcel] =
    useState(false);

  const [importingPhotos, setImportingPhotos] =
    useState(false);

  const [importResult, setImportResult] =
    useState(null);

  const [showAllLowStock, setShowAllLowStock] =
    useState(false);



  // ==========================================================
  // LOGOUT
  // ==========================================================

  const logout =
    useCallback(() => {

      localStorage.removeItem(
        'lor95_admin_token'
      );

      navigate(
        '/admin/login'
      );

    }, [navigate]);


  // ==========================================================
  // LOAD
  // ==========================================================

  const loadData =
    useCallback(
      async () => {

        try {

          const [
            ordersRes,
            productsRes,
            storeSalesRes
          ] = await Promise.all([

            axios.get(
              `${API}/api/orders`,
              {
                headers:
                  authHeaders()
              }
            ),

            axios.get(
              `${API}/api/products?include_out_of_stock=true`
            ),

            axios.get(
              `${API}/api/store-sales`,
              {
                headers:
                  authHeaders()
              }
            )

          ]);


          setOrders(
            ordersRes.data.map(order => ({
              ...order,
              status:
                order.status === 'confirmed'
                  ? 'posted'
                  : order.status
            }))
          );

          setProducts(
            productsRes.data
          );

          setStoreSales(
            storeSalesRes.data
          );

        } catch (err) {

          if (
            err.response?.status
            === 401
          ) {

            logout();

          } else {

            toast.error(
              'Gabim gjatë ngarkimit të të dhënave'
            );
          }

        } finally {

          setLoading(false);
        }
      },
      [logout]
    );


  useEffect(
    () => {

      const token =
        localStorage.getItem(
          'lor95_admin_token'
        );


      if (!token) {

        navigate(
          '/admin/login'
        );

        return;
      }


      loadData();

    },
    [
      navigate,
      loadData
    ]
  );


  // Rifreskon të dhënat pa ndërprerë punën e administratorit.
  // Gjithashtu rifreskon menjëherë kur rikthehet te kjo faqe.
  useEffect(() => {
    const refresh = () => {
      if (
        document.visibilityState === 'visible'
        && localStorage.getItem('lor95_admin_token')
      ) {
        loadData();
      }
    };

    const interval = window.setInterval(refresh, 10000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadData]);


  // ==========================================================
  // IMPORT / EXPORT PRODUCTS
  // ==========================================================

  const downloadBlob = (
    data,
    filename
  ) => {
    const url = window.URL.createObjectURL(
      new Blob([data])
    );

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };


  const downloadImportTemplate = () => {
    try {
      const link = document.createElement('a');

      link.href = '/LOR95-Model-Importi.xlsx';
      link.download = 'LOR95-Model-Importi.xlsx';

      document.body.appendChild(link);
      link.click();
      link.remove();

    } catch (err) {
      toast.error(
        'Nuk u shkarkua modeli Excel'
      );
    }
  };


  const exportProductsExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/api/products/export/excel`,
        {
          headers: authHeaders(),
          responseType: 'blob'
        }
      );

      downloadBlob(
        response.data,
        'LOR95-Produkte-Export.xlsx'
      );

      toast.success(
        'Produktet u eksportuan me sukses'
      );

    } catch (err) {

      let message =
        'Eksporti i produkteve dështoi';

      try {
        if (err?.response?.data instanceof Blob) {
          const errorText =
            await err.response.data.text();

          const parsed =
            JSON.parse(errorText);

          if (parsed?.detail) {
            message = parsed.detail;
          }
        }
        else if (err?.response?.data?.detail) {
          message =
            err.response.data.detail;
        }
      }
      catch (_) {
        // Mbaj mesazhin standard.
      }

      toast.error(message);
    }
  };


  const importExcelFile = async file => {
    if (!file) {
      return;
    }

    setImportingExcel(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/api/products/import/excel`,
        formData,
        {
          headers: authHeaders()
        }
      );

      setImportResult({
        type: 'excel',
        ...response.data
      });

      toast.success(
        `Importi përfundoi: ${response.data.created} të reja, ${response.data.updated} të përditësuara`
      );

      await loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Importi i Excel-it dështoi'
      );
    } finally {
      setImportingExcel(false);
    }
  };


  const importPhotosZip = async (
    file,
    replaceExisting = false
  ) => {
    if (!file) {
      return;
    }

    setImportingPhotos(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append(
        'replace_existing',
        replaceExisting ? 'true' : 'false'
      );

      const response = await axios.post(
        `${API}/api/products/import/photos`,
        formData,
        {
          headers: authHeaders()
        }
      );

      setImportResult({
        type: 'photos',
        ...response.data
      });

      toast.success(
        `${response.data.imported_images} foto u importuan në ${response.data.updated_products} produkte`
      );

      await loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Importi i fotografive dështoi'
      );
    } finally {
      setImportingPhotos(false);
    }
  };


  // ==========================================================
  // ORDER STATUS
  // ==========================================================

  const updateStatus =
    async (
      orderId,
      status
    ) => {

      try {

        await axios.patch(
          `${API}/api/orders/${orderId}`,

          {
            status
          },

          {
            headers:
              authHeaders()
          }
        );


        setOrders(
          prev =>
            prev.map(order => (
              order.order_id
              === orderId

                ? {
                    ...order,
                    status
                  }

                : order
            ))
        );


        toast.success(
          `Statusi u ndryshua në "${STATUS_LABELS[status]}"`
        );


        // Stoku ndryshon automatikisht në backend:
        // pending/cancelled -> posted = ulet
        // posted/delivered -> cancelled/pending = rikthehet
        const [productsRes, storeSalesRes] = await Promise.all([
          axios.get(`${API}/api/products?include_out_of_stock=true`),
          axios.get(`${API}/api/store-sales`, { headers: authHeaders() })
        ]);

        setProducts(productsRes.data);
        setStoreSales(storeSalesRes.data);

      } catch (err) {

        const detail =
          err.response?.data?.detail;


        toast.error(
          typeof detail === 'string'
            ? detail
            : 'Gabim gjatë ndryshimit të statusit'
        );
      }
    };


  // ==========================================================
  // DELETE ORDER(S)
  // ==========================================================

  const deleteOrder =
    async orderId => {

      const confirmed =
        window.confirm(
          'Ta fshij këtë porosi?\n\n' +
          'Porosia do të hiqet nga historiku dhe vlera e saj ' +
          'nuk do të llogaritet më në statistika.\n' +
          'Stoku NUK do të ndryshojë.'
        );

      if (!confirmed) {
        return;
      }

      try {

        await axios.delete(
          `${API}/api/orders/${orderId}`,
          {
            headers:
              authHeaders()
          }
        );

        setOrders(
          prev =>
            prev.filter(
              order =>
                order.order_id !== orderId
            )
        );

        toast.success(
          'Porosia u fshi. Stoku nuk u ndryshua.'
        );

      } catch (err) {

        toast.error(
          err.response?.data?.detail
          ||
          'Porosia nuk u fshi'
        );
      }
    };


  const deleteAllOrders =
    async () => {

      if (orders.length === 0) {
        toast.info(
          'Nuk ka porosi për të fshirë'
        );
        return;
      }

      const confirmed =
        window.confirm(
          `Do të fshihen ${orders.length} porosi.\n\n` +
          'Historiku dhe statistikat e porosive do të pastrohen.\n' +
          'STOKU NUK DO TË NDRYSHOJË.\n\n' +
          'Dëshiron të vazhdosh?'
        );

      if (!confirmed) {
        return;
      }

      const confirmedAgain =
        window.confirm(
          'Konfirmim i fundit: fshiji të gjitha porositë?'
        );

      if (!confirmedAgain) {
        return;
      }

      try {

        const response =
          await axios.delete(
            `${API}/api/orders`,
            {
              headers:
                authHeaders()
            }
          );

        setOrders([]);

        toast.success(
          `${response.data.deleted ?? orders.length} porosi u fshinë. Stoku nuk u ndryshua.`
        );

      } catch (err) {

        toast.error(
          err.response?.data?.detail
          ||
          'Porositë nuk u fshinë'
        );
      }
    };


  // ==========================================================
  // SAVE PRODUCT
  // ==========================================================

  const saveProduct =
    async data => {

      setSaving(true);


      try {

        if (editingProduct) {

          const response =
            await axios.put(
              `${API}/api/products/${editingProduct.id}`,
              data,
              {
                headers:
                  authHeaders()
              }
            );


          setProducts(
            prev =>
              prev.map(product => (
                product.id
                === editingProduct.id

                  ? response.data

                  : product
              ))
          );


          toast.success(
            'Produkti u përditësua me sukses'
          );

        } else {

          const response =
            await axios.post(
              `${API}/api/products`,
              data,
              {
                headers:
                  authHeaders()
              }
            );


          setProducts(
            prev => [
              ...prev,
              response.data
            ]
          );


          toast.success(
            'Produkti u shtua me sukses'
          );
        }


        setDialogOpen(false);

        setEditingProduct(null);

      } catch (err) {

        const detail =
          err.response?.data?.detail;


        toast.error(
          typeof detail === 'string'
            ? detail
            : 'Gabim gjatë ruajtjes së produktit'
        );

      } finally {

        setSaving(false);
      }
    };


  // ==========================================================
  // DELETE PRODUCT
  // ==========================================================

  const deleteProduct =
    async product => {

      if (
        !window.confirm(
          `Jeni i sigurt që doni të fshini "${product.name}"?`
        )
      ) {
        return;
      }


      try {

        await axios.delete(
          `${API}/api/products/${product.id}`,

          {
            headers:
              authHeaders()
          }
        );


        setProducts(
          prev =>
            prev.filter(
              item =>
                item.id
                !== product.id
            )
        );


        toast.success(
          'Produkti u fshi me sukses'
        );

      } catch (err) {

        toast.error(
          'Gabim gjatë fshirjes së produktit'
        );
      }
    };


  // ==========================================================
  // WHATSAPP
  // ==========================================================

  const whatsappLink =
    order => {

      const phone =
        order.phone.replace(
          /[^\d]/g,
          ''
        );


      const items =
        order.items
          .map(item => {

            let details = '';

            if (item.color) {
              details +=
                ` • Ngjyra: ${item.color}`;
            }

            if (item.size) {
              details +=
                ` • Masa: ${item.size}`;
            }

            return (
              `- ${item.name}${details} x${item.quantity}`
            );
          })
          .join('\n');


      const message =
        `Përshëndetje ${order.firstName}! ` +
        `Porosia juaj #${order.order_id} ` +
        `në Lor95 Boutique u pranua.\n\n` +
        `${items}\n\n` +
        `Totali: ${Math.round(order.total / 100)} Lek ` +
        `(Pagesa në Dorëzim)\n\n` +
        `Faleminderit!`;


      return (
        `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
      );
    };


  // ==========================================================
  // OPEN DIALOGS
  // ==========================================================

  const openAdd = () => {

    setEditingProduct(null);

    setDialogOpen(true);
  };


  const openEdit = product => {

    setEditingProduct(product);

    setDialogOpen(true);
  };


  const filteredProducts =
    products.filter(product => {

      const query =
        productSearch
          .trim()
          .toLowerCase();

      if (!query) {
        return true;
      }

      const searchable = [
        product.name,
        product.sku,
        product.category,
        ...(Array.isArray(product.categories)
          ? product.categories
          : []),
        product.gender,
        product.season,
        ...(product.sizes || []),
        ...(product.variants || []).map(
          variant => variant.color
        )
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(query);
    });


  const filteredOrders =
    orders.filter(order => {

      const query =
        orderSearch
          .trim()
          .toLowerCase();

      const searchable = [
        order.order_id,
        order.firstName,
        order.lastName,
        order.email,
        order.phone,
        order.shippingAddress?.address,
        order.shippingAddress?.city,
        order.shippingAddress?.postalCode,
        ...(order.items || []).flatMap(
          item => [
            item.name,
            item.color,
            item.size
          ]
        )
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();


      const searchMatch =
        !query
        ||
        searchable.includes(query);


      const statusMatch =
        orderStatusFilter === 'all'
        ||
        order.status === orderStatusFilter;


      const createdAt =
        order.createdAt
          ? new Date(order.createdAt)
          : null;


      let fromMatch = true;
      let toMatch = true;


      if (
        orderDateFrom
        &&
        createdAt
      ) {
        const fromDate =
          new Date(
            `${orderDateFrom}T00:00:00`
          );

        fromMatch =
          createdAt >= fromDate;
      }


      if (
        orderDateTo
        &&
        createdAt
      ) {
        const toDate =
          new Date(
            `${orderDateTo}T23:59:59.999`
          );

        toMatch =
          createdAt <= toDate;
      }


      return (
        searchMatch
        &&
        statusMatch
        &&
        fromMatch
        &&
        toMatch
      );
    });


  const lowStockVariants =
    products.flatMap(product => {

      if (
        product.variants
        &&
        product.variants.length > 0
      ) {
        return product.variants
          .filter(
            variant =>
              Number(variant.stock) > 0
              &&
              Number(variant.stock) <= 3
          )
          .map(
            variant => ({
              productId: product.id,
              productName: product.name,
              color: variant.color || '',
              size: variant.size || '',
              stock: Number(variant.stock)
            })
          );
      }


      if (
        Number(product.stock) > 0
        &&
        Number(product.stock) <= 3
      ) {
        return [
          {
            productId: product.id,
            productName: product.name,
            color: '',
            size: '',
            stock: Number(product.stock)
          }
        ];
      }


      return [];
    });


  const outOfStockCount =
    products.filter(
      product =>
        Number(product.stock) <= 0
    ).length;


  const deliveredOrders =
    orders.filter(
      order =>
        order.status === 'delivered'
    );


  // Transporti i përket postës dhe nuk është e ardhur e Lor95.
  const orderNetRevenue = order => {
    const subtotal = Number(order.subtotal || 0);
    if (subtotal > 0) return subtotal;
    return Math.max(
      0,
      Number(order.total || 0) - Number(order.shipping || 0)
    );
  };


  const currentMonthKey =
    new Date().toISOString().slice(0, 7);


  const monthlyDeliveredOrders =
    deliveredOrders.filter(
      order =>
        String(order.createdAt || '').slice(0, 7)
        === currentMonthKey
    );


  const monthlyRevenue =
    monthlyDeliveredOrders.reduce(
      (sum, order) =>
        sum + orderNetRevenue(order),
      0
    );


  const productSalesMap = {};

  deliveredOrders.forEach(order => {
    (order.items || []).forEach(item => {
      const key = [
        item.product_id || item.name,
        item.color || '',
        item.size || ''
      ].join('|');

      if (!productSalesMap[key]) {
        productSalesMap[key] = {
          name: item.name,
          color: item.color || '',
          size: item.size || '',
          quantity: 0
        };
      }

      productSalesMap[key].quantity +=
        Number(item.quantity || 0);
    });
  });


  const topSellingProducts =
    Object.values(productSalesMap)
      .sort(
        (a, b) =>
          b.quantity - a.quantity
      )
      .slice(0, 5);


  const pendingCount =
    orders.filter(
      order =>
        order.status === 'pending'
    ).length;


  // ==========================================================
  // PRINT ORDER
  // ==========================================================

  const printOrder = order => {
    const printWindow = window.open(
      '',
      '_blank',
      'width=900,height=900'
    );

    if (!printWindow) {
      toast.error(
        'Browser-i bllokoi dritaren e printimit'
      );
      return;
    }

    const itemsRows = (order.items || [])
      .map(item => {
        const details = [
          item.color ? `Ngjyra: ${item.color}` : '',
          item.size ? `Masa: ${item.size}` : ''
        ]
          .filter(Boolean)
          .join(' • ');

        return `
          <tr>
            <td>
              <strong>${item.name}</strong>
              ${details ? `<div class="muted">${details}</div>` : ''}
            </td>
            <td class="center">${item.quantity}</td>
            <td class="right">${Math.round(item.price / 100)} Lek</td>
            <td class="right">${Math.round(item.price * item.quantity / 100)} Lek</td>
          </tr>
        `;
      })
      .join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="sq">
        <head>
          <meta charset="UTF-8" />
          <title>Porosia ${order.order_id}</title>
          <style>
            * { box-sizing:border-box; }
            body { font-family:Arial,sans-serif; color:#1f2937; margin:0; padding:30px; background:#fff; }
            .page { max-width:820px; margin:0 auto; }
            .header { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:20px; border-bottom:2px solid #111; }
            h1 { margin:0; font-size:28px; }
            .gold { color:#b78332; }
            .muted { color:#6b7280; font-size:12px; margin-top:4px; }
            .order-number { font-size:18px; font-weight:bold; }
            .grid { display:grid; grid-template-columns:1fr 1fr; gap:18px; margin-top:24px; }
            .box { border:1px solid #e5e7eb; padding:15px; }
            .box-title { font-size:11px; text-transform:uppercase; letter-spacing:1px; color:#6b7280; font-weight:bold; margin-bottom:8px; }
            table { width:100%; border-collapse:collapse; margin-top:24px; }
            th,td { border-bottom:1px solid #e5e7eb; padding:12px 8px; text-align:left; }
            th { font-size:11px; text-transform:uppercase; color:#6b7280; }
            .center { text-align:center; }
            .right { text-align:right; }
            .totals { width:330px; margin-left:auto; margin-top:22px; }
            .total-row { display:flex; justify-content:space-between; padding:7px 0; }
            .grand-total { border-top:2px solid #111; margin-top:6px; padding-top:12px; font-size:20px; font-weight:bold; }
            .notes { margin-top:24px; padding:15px; background:#f9fafb; border:1px solid #e5e7eb; }
            .footer { margin-top:30px; text-align:center; font-size:11px; color:#9ca3af; }
            @media print {
              body { padding:0; }
              .page { max-width:none; }
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="header">
              <div>
                <h1>LOR95 <span class="gold">BOUTIQUE</span></h1>
                <div class="muted">Berat, Albania</div>
              </div>
              <div class="right">
                <div class="order-number">${order.order_id}</div>
                <div class="muted">${new Date(order.createdAt).toLocaleString('sq-AL')}</div>
              </div>
            </div>

            <div class="grid">
              <div class="box">
                <div class="box-title">Klienti</div>
                <strong>${order.firstName} ${order.lastName}</strong>
                <div>${order.phone}</div>
                <div>${order.email}</div>
              </div>

              <div class="box">
                <div class="box-title">Adresa e Dorëzimit</div>
                <div>${order.shippingAddress?.address || ''}</div>
                <div>${order.shippingAddress?.city || ''} ${order.shippingAddress?.postalCode || ''}</div>
                <div>${order.shippingAddress?.country || ''}</div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Produkti</th>
                  <th class="center">Sasia</th>
                  <th class="right">Çmimi</th>
                  <th class="right">Totali</th>
                </tr>
              </thead>
              <tbody>${itemsRows}</tbody>
            </table>

            <div class="totals">
              <div class="total-row">
                <span>Nëntotali</span>
                <strong>${Math.round(order.subtotal / 100)} Lek</strong>
              </div>
              <div class="total-row">
                <span>Transporti</span>
                <strong>${Math.round(order.shipping / 100)} Lek</strong>
              </div>
              <div class="total-row grand-total">
                <span>Totali</span>
                <span>${Math.round(order.total / 100)} Lek</span>
              </div>
            </div>

            ${order.notes ? `<div class="notes"><strong>Shënim:</strong> ${order.notes}</div>` : ''}

            <div class="footer">Pagesa në Dorëzim • Lor95 Boutique</div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(
      () => printWindow.print(),
      300
    );
  };


  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {

    return (

      <div className="min-h-screen bg-gray-100 flex items-center justify-center">

        <p className="text-gray-600">
          Duke ngarkuar panelin...
        </p>

      </div>
    );
  }


  // ==========================================================
  // RENDER
  // ==========================================================

  return (

    <div className="min-h-screen bg-gray-100">

      {/* HEADER - i njëjti stil me faqen publike */}

      <header className="
        sticky
        top-0
        z-50
        min-h-[76px]
        border-b
        border-white/20
        bg-black/70
        backdrop-blur-xl
        shadow-[0_14px_45px_rgba(0,0,0,0.25)]
        text-white
      ">

        <div className="
          w-full
          px-4
          sm:px-6
          lg:px-10
          xl:px-14
          min-h-[76px]
          flex
          items-center
          justify-between
        ">

          <div className="flex items-center gap-3">

            <img
              src="/lor95.png"
              alt="Lor95 Boutique"
              className="h-11 sm:h-12 w-auto object-contain"
            />

            <div className="hidden sm:block">

              <p className="font-serif text-white text-xl lg:text-2xl leading-none font-semibold">
                LOR95
              </p>

              <p className="text-[#d0a04a] text-[9px] tracking-[0.30em] mt-1">
                BOUTIQUE
              </p>

            </div>

            <Badge className="
              ml-2
              bg-[#c8953d]/20
              border
              border-[#c8953d]/40
              text-[#e2b663]
              hover:bg-[#c8953d]/20
            ">
              Admin
            </Badge>

          </div>


          <div className="flex items-center gap-1 sm:gap-2">

            <Button
              variant="ghost"
              size="sm"
              onClick={
                () =>
                  setPassDialogOpen(
                    true
                  )
              }
              className="text-white/90 hover:text-[#d1a24e] hover:bg-white/10"
            >

              <KeyRound className="h-4 w-4 mr-1" />

              <span className="hidden sm:inline">
                Fjalëkalimi
              </span>

            </Button>


            <Button
              variant="ghost"
              size="sm"
              onClick={loadData}
              className="text-white/90 hover:text-[#d1a24e] hover:bg-white/10"
            >

              <RefreshCw className="h-4 w-4 mr-1" />

              <span className="hidden sm:inline">
                Rifresko
              </span>

            </Button>


            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-white/90 hover:text-[#d1a24e] hover:bg-white/10"
            >

              <LogOut className="h-4 w-4 mr-1" />

              <span className="hidden sm:inline">
                Dil
              </span>

            </Button>

          </div>

        </div>

      </header>


      <main className="
        max-w-7xl
        mx-auto
        px-4
        sm:px-6
        lg:px-8
        py-8
      ">

        {/* STATS */}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">

          <Card className="border-none shadow-sm">

            <CardContent className="p-5">

              <p className="text-sm text-gray-500">
                Porosi Gjithsej
              </p>

              <p className="text-3xl font-bold text-gray-900">
                {orders.length}
              </p>

            </CardContent>

          </Card>


          <Card className="border-none shadow-sm">

            <CardContent className="p-5">

              <p className="text-sm text-gray-500">
                Në Pritje
              </p>

              <p className="text-3xl font-bold text-amber-600">
                {pendingCount}
              </p>

            </CardContent>

          </Card>


          <Card className="border-none shadow-sm">

            <CardContent className="p-5">

              <p className="text-sm text-gray-500">
                Produkte
              </p>

              <p className="text-3xl font-bold text-gray-900">
                {products.length}
              </p>

            </CardContent>

          </Card>


          <Card className="border-none shadow-sm">

            <CardContent className="p-5">

              <p className="text-sm text-gray-500">
                Të Ardhura (Dorëzuar)
              </p>

              <p className="text-3xl font-bold text-green-600">

                {
                  Math.round(
                    orders
                      .filter(
                        order =>
                          order.status
                          === 'delivered'
                      )
                      .reduce(
                        (
                          sum,
                          order
                        ) =>
                          sum + orderNetRevenue(order),
                        0
                      )
                    / 100
                  )
                }
                {' '}
                Lek

              </p>

            </CardContent>

          </Card>

        </div>


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">

          <Card className="border-none shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">
                    Shitje të Dorëzuara
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {deliveredOrders.length}
                  </p>
                </div>

                <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>


          <Card className="border-none shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">
                    Xhiro e Këtij Muaji
                  </p>
                  <p className="text-3xl font-bold text-green-700 mt-1">
                    {Math.round(monthlyRevenue / 100)} Lek
                  </p>
                </div>

                <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>


          <Card className="border-none shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-gray-500 mb-3">
                Produktet më të Shitura
              </p>

              {topSellingProducts.length === 0 ? (
                <p className="text-sm text-gray-400">
                  Ende nuk ka të dhëna.
                </p>
              ) : (
                <div className="space-y-2">
                  {topSellingProducts
                    .slice(0, 3)
                    .map((item, index) => (
                      <div
                        key={`${item.name}-${item.color}-${item.size}-${index}`}
                        className="flex items-center justify-between gap-3 text-sm"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 line-clamp-1">
                            {index + 1}. {item.name}
                          </p>

                          <p className="text-xs text-gray-500">
                            {[item.color, item.size]
                              .filter(Boolean)
                              .join(' • ')}
                          </p>
                        </div>

                        <strong className="text-gray-900 whitespace-nowrap">
                          {item.quantity} copë
                        </strong>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>

        </div>


        {(lowStockVariants.length > 0 || outOfStockCount > 0) && (

          <Card className="
            mb-8
            border
            border-amber-200
            bg-amber-50
            shadow-sm
          ">

            <CardContent className="p-5">

              <div className="
                flex
                flex-col
                lg:flex-row
                lg:items-start
                lg:justify-between
                gap-4
              ">

                <div>

                  <div className="flex items-center gap-2">

                    <Package className="h-5 w-5 text-amber-700" />

                    <h2 className="font-bold text-amber-900">
                      Njoftim për Stok të Ulët
                    </h2>

                  </div>

                  <p className="text-sm text-amber-800 mt-1">
                    {lowStockVariants.length} variant
                    {lowStockVariants.length === 1 ? '' : 'e'} me 1–3 copë
                    {outOfStockCount > 0
                      ? ` • ${outOfStockCount} produkt${outOfStockCount === 1 ? '' : 'e'} pa stok`
                      : ''}
                  </p>

                </div>


                {lowStockVariants.length > 0 && (

                  <div className="
                    grid
                    grid-cols-1
                    sm:grid-cols-2
                    lg:grid-cols-3
                    gap-2
                    flex-1
                    lg:max-w-3xl
                  ">

                    {(showAllLowStock
                      ? lowStockVariants
                      : lowStockVariants.slice(0, 6)
                    )
                      .map(
                        (
                          item,
                          index
                        ) => (

                          <div
                            key={`${item.productId}-${item.color}-${item.size}-${index}`}
                            className="
                              bg-white
                              border
                              border-amber-200
                              px-3
                              py-2
                              text-xs
                            "
                          >

                            <p className="font-semibold text-gray-900 line-clamp-1">
                              {item.productName}
                            </p>

                            <p className="text-gray-600 mt-0.5">
                              {item.color && (
                                <>
                                  {item.color}
                                  {' '}
                                </>
                              )}

                              {item.size && (
                                <>
                                  • {item.size}
                                  {' '}
                                </>
                              )}

                              •
                              {' '}
                              <strong className="text-amber-700">
                                {item.stock} copë
                              </strong>
                            </p>

                          </div>
                        )
                      )}

                    {lowStockVariants.length > 6 && (

                      <button
                        type="button"
                        onClick={
                          () =>
                            setShowAllLowStock(
                              prev => !prev
                            )
                        }
                        className="
                          bg-white
                          border
                          border-amber-300
                          px-3
                          py-2
                          text-xs
                          flex
                          items-center
                          justify-center
                          text-amber-800
                          font-semibold
                          hover:bg-amber-100
                          transition-colors
                        "
                      >

                        {showAllLowStock
                          ? 'Shfaq më pak'
                          : `+${lowStockVariants.length - 6} variante të tjera`
                        }

                      </button>
                    )}

                  </div>
                )}

              </div>

            </CardContent>

          </Card>
        )}


        <Tabs defaultValue="orders">

          <TabsList className="mb-6 flex h-auto w-full justify-start overflow-x-auto rounded-none">

            <TabsTrigger
              value="orders"
              className="shrink-0 rounded-none"
            >

              <ShoppingBag className="h-4 w-4 mr-2" />

              Porositë ({orders.length})

            </TabsTrigger>


            <TabsTrigger
              value="inventory"
              className="shrink-0 rounded-none"
            >

              <Store className="h-4 w-4 mr-2" />

              Inventari ({storeSales.length})

            </TabsTrigger>


            <TabsTrigger
              value="products"
              className="shrink-0 rounded-none"
            >

              <Package className="h-4 w-4 mr-2" />

              Produktet ({products.length})

            </TabsTrigger>


            <TabsTrigger
              value="imports"
              className="shrink-0 rounded-none"
            >

              <FileSpreadsheet className="h-4 w-4 mr-2" />

              Import / Export

            </TabsTrigger>

          </TabsList>


          {/* =================================================
              ORDERS
          ================================================== */}

          <TabsContent
            value="orders"
            className="space-y-4"
          >

            <Card className="border-none shadow-sm">

              <CardContent className="p-4">

                <div className="
                  grid
                  grid-cols-1
                  md:grid-cols-2
                  xl:grid-cols-5
                  gap-3
                  items-end
                ">

                  <div className="xl:col-span-2">

                    <Label>
                      Kërko Porosi
                    </Label>

                    <div className="relative mt-1">

                      <Search className="
                        absolute
                        left-3
                        top-1/2
                        -translate-y-1/2
                        h-4
                        w-4
                        text-gray-400
                      " />

                      <Input
                        value={orderSearch}
                        onChange={
                          e =>
                            setOrderSearch(
                              e.target.value
                            )
                        }
                        placeholder="Nr. porosie, emër, telefon, produkt..."
                        className="pl-9 rounded-none"
                      />

                    </div>

                  </div>


                  <div>

                    <Label>
                      Statusi
                    </Label>

                    <Select
                      value={orderStatusFilter}
                      onValueChange={
                        setOrderStatusFilter
                      }
                    >

                      <SelectTrigger className="mt-1 rounded-none">
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>

                        <SelectItem value="all">
                          Të gjitha
                        </SelectItem>

                        {Object.entries(
                          STATUS_LABELS
                        ).map(
                          ([value, label]) => (

                            <SelectItem
                              key={value}
                              value={value}
                            >
                              {label}
                            </SelectItem>
                          )
                        )}

                      </SelectContent>

                    </Select>

                  </div>


                  <div>

                    <Label>
                      Nga Data
                    </Label>

                    <Input
                      type="date"
                      value={orderDateFrom}
                      onChange={
                        e =>
                          setOrderDateFrom(
                            e.target.value
                          )
                      }
                      className="mt-1 rounded-none"
                    />

                  </div>


                  <div>

                    <Label>
                      Deri më
                    </Label>

                    <Input
                      type="date"
                      value={orderDateTo}
                      onChange={
                        e =>
                          setOrderDateTo(
                            e.target.value
                          )
                      }
                      className="mt-1 rounded-none"
                    />

                  </div>

                </div>


                <div className="
                  flex
                  flex-wrap
                  items-center
                  justify-between
                  gap-3
                  mt-4
                  pt-3
                  border-t
                  border-gray-100
                ">

                  <p className="text-sm text-gray-500">
                    Po shfaqen
                    {' '}
                    <strong className="text-gray-900">
                      {filteredOrders.length}
                    </strong>
                    {' '}
                    nga
                    {' '}
                    <strong className="text-gray-900">
                      {orders.length}
                    </strong>
                    {' '}
                    porosi
                  </p>


                  {(
                    orderSearch
                    ||
                    orderStatusFilter !== 'all'
                    ||
                    orderDateFrom
                    ||
                    orderDateTo
                  ) && (

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={
                        () => {
                          setOrderSearch('');
                          setOrderStatusFilter('all');
                          setOrderDateFrom('');
                          setOrderDateTo('');
                        }
                      }
                      className="rounded-none"
                    >
                      Pastro Filtrat
                    </Button>
                  )}


                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={deleteAllOrders}
                    disabled={orders.length === 0}
                    className="
                      rounded-none
                      border-red-300
                      text-red-700
                      hover:bg-red-50
                    "
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Fshi të gjitha
                  </Button>

                </div>

              </CardContent>

            </Card>


            {orders.length === 0 ? (

              <Card className="border-none shadow-sm">

                <CardContent className="p-12 text-center text-gray-500">

                  Ende nuk ka porosi.

                </CardContent>

              </Card>

            ) : filteredOrders.length === 0 ? (

              <Card className="border-none shadow-sm">

                <CardContent className="p-12 text-center text-gray-500">

                  Nuk u gjet asnjë porosi me filtrat e zgjedhur.

                </CardContent>

              </Card>

            ) : (

              filteredOrders.map(order => (

                <Card
                  key={order.order_id}

                  className="border-none shadow-sm"
                >

                  <CardContent className="p-6">

                    <div className="
                      flex
                      flex-wrap
                      items-start
                      justify-between
                      gap-4
                      mb-4
                    ">

                      <div>

                        <div className="flex items-center gap-3">

                          <h3 className="text-lg font-bold text-gray-900">

                            #{order.order_id}

                          </h3>


                          <span className={`
                            text-xs
                            font-semibold
                            px-3
                            py-1
                            rounded-full
                            ${STATUS_COLORS[order.status]}
                          `}>

                            {STATUS_LABELS[order.status]}

                          </span>

                        </div>


                        <p className="text-sm text-gray-500 mt-1">

                          {
                            new Date(
                              order.createdAt
                            ).toLocaleString(
                              'sq-AL'
                            )
                          }

                        </p>

                      </div>


                      <div className="text-right">

                        <p className="text-2xl font-bold text-gray-900">

                          {
                            Math.round(
                              order.total / 100
                            )
                          }
                          {' '}
                          Lek

                        </p>

                        <p className="text-xs text-gray-500">
                          Pagesa në Dorëzim
                        </p>

                      </div>

                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

                      <div className="bg-gray-50 p-4">

                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                          Klienti
                        </p>

                        <p className="font-semibold text-gray-900">

                          {order.firstName}
                          {' '}
                          {order.lastName}

                        </p>

                        <p className="text-sm text-gray-600">
                          {order.email}
                        </p>

                        <p className="text-sm text-gray-900 font-medium">
                          {order.phone}
                        </p>

                      </div>


                      <div className="bg-gray-50 p-4">

                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                          Adresa e Dorëzimit
                        </p>

                        <p className="text-sm text-gray-700">

                          {order.shippingAddress.address},
                          {' '}
                          {order.shippingAddress.city}
                          {' '}
                          {order.shippingAddress.postalCode},
                          {' '}
                          {order.shippingAddress.country}

                        </p>


                        {order.notes && (

                          <p className="text-sm text-gray-500 italic mt-1">

                            Shënim:
                            {' '}
                            {order.notes}

                          </p>

                        )}

                      </div>

                    </div>


                    {/* ORDER ITEMS */}

                    <div className="mb-4">

                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Produktet
                      </p>


                      <div className="space-y-2">

                        {order.items.map(
                          (
                            item,
                            index
                          ) => (

                            <div
                              key={index}

                              className="flex items-center gap-3"
                            >

                              <img
                                src={imgUrl(item.image)}

                                alt={item.name}

                                className="w-10 h-10 object-cover"
                              />


                              <span className="text-sm text-gray-700 flex-grow">

                                {item.name}

                                {item.color && (
                                  <>
                                    {' '}
                                    • Ngjyra:
                                    {' '}
                                    {item.color}
                                  </>
                                )}

                                {item.size && (
                                  <>
                                    {' '}
                                    • Masa:
                                    {' '}
                                    {item.size}
                                  </>
                                )}

                                {' '}
                                ×
                                {' '}
                                {item.quantity}

                              </span>


                              <span className="text-sm font-medium">

                                {
                                  Math.round(
                                    item.price
                                    * item.quantity
                                    / 100
                                  )
                                }
                                {' '}
                                Lek

                              </span>

                            </div>

                          )
                        )}

                      </div>

                    </div>


                    {/* ORDER ACTIONS */}

                    <div className="
                      flex
                      flex-wrap
                      items-center
                      gap-3
                      pt-4
                      border-t
                      border-gray-100
                    ">

                      <Select
                        value={order.status}

                        onValueChange={
                          value =>
                            updateStatus(
                              order.order_id,
                              value
                            )
                        }
                      >

                        <SelectTrigger className="w-44 rounded-none">

                          <SelectValue />

                        </SelectTrigger>

                        <SelectContent>

                          {Object.entries(
                            STATUS_LABELS
                          ).map(
                            (
                              [
                                value,
                                label
                              ]
                            ) => (

                              <SelectItem
                                key={value}
                                value={value}
                              >

                                {label}

                              </SelectItem>

                            )
                          )}

                        </SelectContent>

                      </Select>


                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={
                          () =>
                            printOrder(order)
                        }
                        className="
                          rounded-none
                          border-gray-300
                          text-gray-700
                          hover:bg-gray-50
                        "
                      >

                        <Printer className="h-4 w-4 mr-1" />

                        Printo

                      </Button>


                      <Button
                        asChild

                        variant="outline"

                        size="sm"

                        className="
                          rounded-none
                          border-green-600
                          text-green-700
                          hover:bg-green-50
                        "
                      >

                        <a
                          href={whatsappLink(order)}

                          target="_blank"

                          rel="noopener noreferrer"
                        >

                          <MessageCircle className="h-4 w-4 mr-1" />

                          WhatsApp

                        </a>

                      </Button>


                      <Button
                        asChild

                        variant="outline"

                        size="sm"

                        className="rounded-none"
                      >

                        <a href={`tel:${order.phone}`}>

                          <Phone className="h-4 w-4 mr-1" />

                          Telefono

                        </a>

                      </Button>


                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={
                          () =>
                            deleteOrder(
                              order.order_id
                            )
                        }
                        className="
                          rounded-none
                          border-red-300
                          text-red-700
                          hover:bg-red-50
                        "
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Fshi
                      </Button>

                    </div>

                  </CardContent>

                </Card>

              ))

            )}

          </TabsContent>


          {/* =================================================
              STORE INVENTORY / SALES
          ================================================== */}

          <TabsContent
            value="inventory"
            className="space-y-6"
          >
            <StoreInventoryTab
              sales={storeSales}
              products={products}
              reload={loadData}
            />
          </TabsContent>


          {/* =================================================
              PRODUCTS
          ================================================== */}

          {/* =================================================
              IMPORT / EXPORT
          ================================================== */}

          <TabsContent
            value="imports"
            className="space-y-6"
          >

            <Card className="border-none shadow-sm">

              <CardContent className="p-6">

                <div className="flex items-start gap-3 mb-6">

                  <FileSpreadsheet className="h-6 w-6 text-[#c8953d] mt-1" />

                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Import / Export Produktesh
                    </h2>

                    <p className="text-sm text-gray-500 mt-1">
                      Shtoni ose përditësoni qindra produkte me Excel dhe lidhni fotografitë sipas SKU + ngjyrë.
                    </p>
                  </div>

                </div>


                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                  <div className="border border-gray-200 p-5 space-y-4">

                    <div>
                      <h3 className="font-semibold text-gray-900">
                        1. Excel i Produkteve
                      </h3>

                      <p className="text-sm text-gray-500 mt-1">
                        Një rresht = një kombinim ngjyrë + masë + stok. Rreshtat me të njëjtin SKU bashkohen automatikisht.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">

                      <Button
                        type="button"
                        variant="outline"
                        onClick={downloadImportTemplate}
                        className="rounded-none"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Shkarko Model Excel
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={exportProductsExcel}
                        className="rounded-none"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Eksporto Produktet
                      </Button>

                    </div>

                    <label className="block">
                      <span className="text-sm font-medium text-gray-700">
                        Importo Excel (.xlsx)
                      </span>

                      <input
                        type="file"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        disabled={importingExcel}
                        onChange={event => {
                          const file = event.target.files?.[0];
                          importExcelFile(file);
                          event.target.value = '';
                        }}
                        className="mt-2 block w-full text-sm border border-gray-300 p-3"
                      />
                    </label>

                    {importingExcel && (
                      <p className="text-sm text-[#a77426]">
                        Duke importuar produktet...
                      </p>
                    )}

                  </div>


                  <div className="border border-gray-200 p-5 space-y-4">

                    <div className="flex items-start gap-3">
                      <Images className="h-5 w-5 text-[#c8953d] mt-0.5" />

                      <div>
                        <h3 className="font-semibold text-gray-900">
                          2. Fotografitë ZIP
                        </h3>

                        <p className="text-sm text-gray-500 mt-1">
                          Mund të importoni sa foto të dëshironi për të njëjtën ngjyrë.
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 p-4 text-sm space-y-1">
                      <p className="font-semibold">Emërtimi i fotove:</p>
                      <p className="font-mono text-xs">ATL001__E bardhë__01.jpg</p>
                      <p className="font-mono text-xs">ATL001__E bardhë__02.jpg</p>
                      <p className="font-mono text-xs">ATL001__E zezë__01.jpg</p>
                    </div>

                    <label className="block">
                      <span className="text-sm font-medium text-gray-700">
                        Importo ZIP me foto
                      </span>

                      <input
                        type="file"
                        accept=".zip,application/zip"
                        disabled={importingPhotos}
                        onChange={event => {
                          const file = event.target.files?.[0];
                          importPhotosZip(file, false);
                          event.target.value = '';
                        }}
                        className="mt-2 block w-full text-sm border border-gray-300 p-3"
                      />
                    </label>

                    <p className="text-xs text-gray-500">
                      Importi normal i shton fotot e reja pa fshirë fotot ekzistuese.
                    </p>

                    {importingPhotos && (
                      <p className="text-sm text-[#a77426]">
                        Duke optimizuar dhe importuar fotografitë...
                      </p>
                    )}

                  </div>

                </div>


                {importResult && (

                  <div className="mt-6 border border-gray-200 bg-gray-50 p-5">

                    <h3 className="font-semibold text-gray-900 mb-2">
                      Rezultati i Importit
                    </h3>

                    {importResult.type === 'excel' ? (
                      <p className="text-sm text-gray-700">
                        Të reja: {importResult.created} • Përditësuara: {importResult.updated} • Gabime: {importResult.error_count}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-700">
                        Foto: {importResult.imported_images} • Produkte: {importResult.updated_products} • Gabime: {importResult.error_count}
                      </p>
                    )}

                    {importResult.errors?.length > 0 && (
                      <div className="mt-3 max-h-48 overflow-y-auto space-y-1">
                        {importResult.errors.slice(0, 25).map((error, index) => (
                          <p
                            key={`${error.row || error.file || index}-${index}`}
                            className="text-xs text-red-700"
                          >
                            {error.row ? `Rreshti ${error.row}: ` : ''}
                            {error.file ? `${error.file}: ` : ''}
                            {error.error}
                          </p>
                        ))}
                      </div>
                    )}

                  </div>

                )}

              </CardContent>

            </Card>

          </TabsContent>


          <TabsContent value="products">

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">

              <div className="relative w-full sm:max-w-md">

                <Search className="
                  absolute
                  left-3
                  top-1/2
                  -translate-y-1/2
                  h-4
                  w-4
                  text-gray-400
                " />

                <Input
                  value={productSearch}
                  onChange={
                    e =>
                      setProductSearch(
                        e.target.value
                      )
                  }
                  placeholder="Kërko produkt sipas emrit, kategorisë, ngjyrës..."
                  className="pl-9 rounded-none"
                />

              </div>


              <Button
                onClick={openAdd}

                className="
                  bg-gray-900
                  hover:bg-gray-800
                  text-white
                  rounded-none
                "
              >

                <Plus className="h-4 w-4 mr-2" />

                Shto Produkt

              </Button>

            </div>


            <div className="
              grid
              grid-cols-1
              sm:grid-cols-2
              lg:grid-cols-3
              gap-4
            ">

              {filteredProducts.map(product => {

                const colors =
                  Array.from(
                    new Set(
                      (
                        product.variants
                        || []
                      )
                        .map(
                          variant =>
                            variant.color
                        )
                        .filter(Boolean)
                    )
                  );


                return (

                  <Card
                    key={product.id}

                    className="
                      border-none
                      shadow-sm
                      overflow-hidden
                    "
                  >

                    <CardContent className="p-0">

                      <div className="flex">

                        <img
                          src={
                            imgUrl(
                              product.image
                              || product.images?.[0]?.url
                            )
                          }

                          alt={product.name}

                          className="
                            w-24
                            h-36
                            object-cover
                            flex-shrink-0
                          "
                        />


                        <div className="p-4 flex-grow min-w-0">

                          <h3 className="
                            font-semibold
                            text-gray-900
                            text-sm
                            line-clamp-1
                          ">

                            {product.name}

                          </h3>


                          {product.sku && (

                            <p className="text-[11px] font-mono text-gray-400 mt-1">
                              SKU: {product.sku}
                            </p>

                          )}


                          <p className="text-xs text-gray-500 mt-1">

                            {
                              Array.isArray(product.categories)
                              && product.categories.length
                                ? product.categories.join(' • ')
                                : product.category
                            }
                            {' • '}
                            {product.gender}
                            {' • '}
                            {product.season}

                          </p>


                          <div className="flex items-center gap-3 mt-1">

                            <div>

                              {(
                                product.salePrice != null
                                && product.salePrice > 0
                                && product.salePrice < product.price
                              ) ? (
                                <div className="flex items-baseline gap-2">

                                  <span className="text-sm text-gray-400 line-through">
                                    {Math.round(product.price / 100)} Lek
                                  </span>

                                  <span className="text-lg font-bold text-red-600">
                                    {Math.round(product.salePrice / 100)} Lek
                                  </span>

                                </div>
                              ) : (
                                <p className="text-lg font-bold text-gray-900">
                                  {Math.round(product.price / 100)} Lek
                                </p>
                              )}

                            </div>


                            <span
                              className={`
                                text-xs
                                font-semibold
                                px-2
                                py-0.5

                                ${
                                  product.stock <= 0

                                    ? 'bg-red-100 text-red-700'

                                    : product.stock <= 3

                                      ? 'bg-amber-100 text-amber-700'

                                      : 'bg-green-100 text-green-700'
                                }
                              `}
                            >

                              Stoku:
                              {' '}
                              {product.stock}

                            </span>

                            {(
                              Number(product.stock) > 0
                              &&
                              Number(product.stock) <= 3
                            ) && (

                              <span className="
                                text-xs
                                font-semibold
                                px-2
                                py-0.5
                                bg-amber-100
                                text-amber-800
                              ">
                                STOK I ULËT
                              </span>
                            )}

                          </div>


                          {colors.length > 0 && (

                            <p className="text-xs text-gray-500 mt-1 line-clamp-1">

                              Ngjyrat:
                              {' '}
                              {colors.join(', ')}

                            </p>

                          )}


                          {product.sizes?.length > 0 && (

                            <p className="text-xs text-gray-400 mt-1 line-clamp-1">

                              Masat:
                              {' '}
                              {product.sizes.join(', ')}

                            </p>

                          )}


                          <div className="flex gap-2 mt-3">

                            <Button
                              size="sm"

                              variant="outline"

                              onClick={
                                () =>
                                  openEdit(
                                    product
                                  )
                              }

                              className="rounded-none h-8"
                            >

                              <Pencil className="h-3 w-3 mr-1" />

                              Modifiko

                            </Button>


                            <Button
                              size="sm"

                              variant="outline"

                              onClick={
                                () =>
                                  deleteProduct(
                                    product
                                  )
                              }

                              className="
                                rounded-none
                                h-8
                                border-red-300
                                text-red-600
                                hover:bg-red-50
                              "
                            >

                              <Trash2 className="h-3 w-3 mr-1" />

                              Fshi

                            </Button>

                          </div>

                        </div>

                      </div>

                    </CardContent>

                  </Card>

                );
              })}

            </div>

          </TabsContent>

        </Tabs>

      </main>


      {/* =====================================================
          PRODUCT DIALOG
      ====================================================== */}

      <Dialog
        open={dialogOpen}

        onOpenChange={
          open => {

            setDialogOpen(open);

            if (!open) {
              setEditingProduct(null);
            }
          }
        }
      >

        <DialogContent className="
          max-w-4xl
          max-h-[92vh]
          overflow-y-auto
          rounded-none
        ">

          <DialogHeader>

            <DialogTitle className="font-serif text-2xl">

              {
                editingProduct
                  ? 'Modifiko Produktin'
                  : 'Shto Produkt të Ri'
              }

            </DialogTitle>

          </DialogHeader>


          <ProductForm
            key={
              editingProduct?.id
              || 'new'
            }

            initial={
              editingProduct

                ? productToForm(
                    editingProduct
                  )

                : createEmptyProduct()
            }

            onSave={
              saveProduct
            }

            onCancel={
              () => {

                setDialogOpen(false);

                setEditingProduct(null);
              }
            }

            saving={saving}
          />

        </DialogContent>

      </Dialog>


      <ChangePasswordDialog
        open={passDialogOpen}

        onOpenChange={
          setPassDialogOpen
        }
      />

    </div>
  );
}
